import {
  Children,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
  cloneElement,
  isValidElement,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { type Layout, type PanelSize, useDefaultLayout, useGroupRef } from 'react-resizable-panels';
import { useDropzone } from 'react-dropzone';
import { Download, FolderIcon } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import {
  INSPECTOR_DEFAULT_SIZE_PERCENT,
  INSPECTOR_MIN_WIDTH,
  MAIN_CONTENT_MIN_WIDTH,
  SEARCH_OPTIONS_MIN_WIDTH,
  SIDEBAR_MIN_WIDTH,
} from '@/components/layouts/panel';
import { type ContributorMetadataDefinition } from '@/components/inspector/metadata-accordion/metadata-panel-form';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable';
import { useUserSettingsStore } from '@/hooks/data/settings/useUserSettingsStore';
import { useFixedPanelsGroupMinWidth } from '@/hooks/useFixedPanelsGroupMinWidth';
import { type GalleryLayoutProps } from '@/components/layouts/gallery-layout';
import { SideNavigation } from '@/components/side-navigation/side-navigation';
import { ModalId, useCurrentModalId } from '@/hooks/data/modal/useModalStore';
import { UploadDialog } from '@/components/upload-dialog/upload-dialog';
import { useUploadStore } from '@/hooks/data/inspector/useUploadStore';
import { SearchOptions } from '@/components/sidebar/search-options';
import { useIsSearching } from '@/hooks/data/search/useIsSearching';
import { useTreeStore } from '@/hooks/data/tree/useTreeStore';
import { Inspector } from '@/components/inspector/inspector';
import { useCapabilities } from '@/hooks/useCapabilities';
import { DAMNodeType, type TheNode } from '@/types/tree';
import { useCurrentPage } from '@/hooks/useCurrentPage';
import { Sidebar } from '@/components/sidebar/sidebar';
import { Navigation } from '@/components/navigation';
import { type GetAssets } from '@/types/asset';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

export const DamLayout = ({
  children,
  assets,
  node,
  singleAssetShare,
  singleAssetView,
  parentPath,
  refetchNode,
  pageFooter,
  isUpload,
  uploadTabContent,
  contributorMetadataDefinitions,
  hideInspector,
}: PropsWithChildren<{
  assets?: GetAssets;
  node?: TheNode;
  singleAssetShare?: boolean;
  singleAssetView?: boolean;
  parentPath?: Array<string>;
  refetchNode?: () => void;
  /** Renders full-width below the main content and Inspector, for a page-specific action bar. */
  pageFooter?: ReactNode;
  isUpload?: boolean;
  uploadTabContent?: ReactNode;
  contributorMetadataDefinitions?: Array<ContributorMetadataDefinition>;
  /** Forces the Inspector panel off regardless of the user's showInspector preference. */
  hideInspector?: boolean;
}>) => {
  const selectedNode = useTreeStore((state) => state.selectedNode);
  const findNodePath = useTreeStore(useShallow((state) => state.findNodePath));
  const currentModal = useCurrentModalId();

  const tUpload = useTranslations('upload');
  const showInspector = useUserSettingsStore((s) => s.showInspector);
  const setShowInspector = useUserSettingsStore((s) => s.setShowInspector);

  const { isManageWelcomePage, isBrowsePage, isHomePage } = useCurrentPage();
  const search = useIsSearching();
  const { canViewSidePanels } = useCapabilities();
  // Per-field selectors, not a bare useUploadStore(): uploads write once per file chunk,
  // and a no-selector subscription re-renders DamLayout — and with it the unmemoized
  // Sidebar and SideNavigation — on every one of those writes. Actions are stable store
  // references, so only the real state read below can trigger a re-render.
  const uploadModalOpen = useUploadStore((state) => state.uploadModalOpen);
  const onDrop = useUploadStore((state) => state.onDrop);
  const setUploadFolderPath = useUploadStore((state) => state.setUploadFolderPath);
  const getActiveUploadSession = useUploadStore((state) => state.getActiveUploadSession);
  const setUploadModalOpen = useUploadStore((state) => state.setUploadModalOpen);
  const setSelectedAlbums = useUploadStore((state) => state.setSelectedAlbums);
  const clearSelectedAlbums = useUploadStore((state) => state.clearSelectedAlbums);
  const setReusableTreeSelectedFolder = useTreeStore((state) => state.setReusableTreeSelectedFolder);
  const setReusableTreeSelection = useTreeStore((state) => state.setReusableTreeSelection);

  const isDropzoneDisabled = useMemo(
    () => !isBrowsePage || currentModal === ModalId.UploadAssetVersion,
    [isBrowsePage, currentModal],
  );

  const { getRootProps, isDragActive } = useDropzone({
    multiple: true,
    disabled: isDropzoneDisabled,
    maxFiles: undefined,
    noClick: true,
    noKeyboard: true,
    onDrop: (acceptedFiles) => {
      const { uploadSessionId } = getActiveUploadSession();

      if (selectedNode?.node) {
        if (selectedNode.type === DAMNodeType.Album) {
          const namePath = findNodePath(selectedNode.node.id, 'name') as Array<string> | undefined;
          const idPath = findNodePath(selectedNode.node.id, 'id') as Array<string> | undefined;

          const parentNodePath = namePath && namePath.length > 1 ? namePath.slice(0, -1).join('/') : '';
          const parentId = idPath && idPath.length > 1 ? idPath[idPath.length - 2] : undefined;

          // Use parent folder as upload folder
          if (parentId) {
            setUploadFolderPath(uploadSessionId, parentNodePath ? `/${parentNodePath}` : '');
            setReusableTreeSelectedFolder(parentId);
          } else {
            // Fallback if no parent folder is found
            setUploadFolderPath(uploadSessionId, '');
            setReusableTreeSelectedFolder(undefined);
            setReusableTreeSelection(undefined);
          }

          // Preselect the album
          setSelectedAlbums(uploadSessionId, [{ id: selectedNode.node.id, label: selectedNode.node.name }]);
        } else {
          const pathArray = findNodePath(selectedNode.node.id, 'name');
          const path = pathArray?.join('/');
          setUploadFolderPath(uploadSessionId, path ? `/${path}` : '');
          setReusableTreeSelectedFolder(selectedNode.node.id);

          // Clear any previous album preselection
          clearSelectedAlbums(uploadSessionId);
        }

        setUploadModalOpen(true);
      } else {
        setUploadFolderPath(uploadSessionId, '');
        setReusableTreeSelectedFolder(undefined);
        setReusableTreeSelection(undefined);
        clearSelectedAlbums(uploadSessionId);
        setUploadModalOpen(true);
      }

      void onDrop(acceptedFiles, uploadSessionId);
    },
  });

  const dropzoneProps = !uploadModalOpen ? getRootProps() : {};

  /** A plain snapshot, not a subscription — this key tags along on whichever render
   * DamLayout already does for other reasons. Creating the session belongs in the
   * effect below: doing it here, inline, would mutate the store mid-render. */
  const activeUploadSessionId = Object.entries(useUploadStore.getState().uploads).find(([, s]) => s.active)?.[0];

  useEffect(() => {
    if (!activeUploadSessionId) {
      getActiveUploadSession();
    }
  }, [activeUploadSessionId, getActiveUploadSession]);

  useEffect(() => {
    const handleDragEvent = (e: any) => {
      const isInsideUploadDropzone =
        (e.target instanceof Element && e.target.closest('#upload-dropzone')) ||
        (e.target instanceof Element && e.target.closest('#version-upload-dropzone'));
      if (isDropzoneDisabled && !isInsideUploadDropzone) {
        e.preventDefault();
        e.dataTransfer!.effectAllowed = 'none';
        e.dataTransfer!.dropEffect = 'none';
      }
    };

    const events: Array<keyof WindowEventMap> = ['dragenter', 'dragover', 'drop'];

    events.forEach((event) => {
      window.addEventListener(event, handleDragEvent, false);
    });

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleDragEvent, false);
      });
    };
  }, [isDropzoneDisabled]);

  // The inspector panel is conditionally rendered (not mounted-then-collapsed). An unmounted
  // panel can't be re-expanded by a v4 group relayout, so no stale "grey rectangle" can appear
  // on the home page. showInspector is the user's preference; it stays true while hidden by page
  // context so the inspector reopens automatically when returning to a folder/search view.
  const displayInspector = !hideInspector && showInspector && (!isHomePage || search);
  const canDisplayInspector = !hideInspector && (!isHomePage || search);
  const showSideRail = canViewSidePanels && !singleAssetView && !singleAssetShare;
  const panelIds = useMemo(
    () => [
      ...(showSideRail ? ['sidebar'] : []),
      ...(showSideRail && search ? ['search-options'] : []),
      'main-content',
      ...(!isManageWelcomePage && displayInspector ? ['inspector'] : []),
    ],
    [displayInspector, isManageWelcomePage, search, showSideRail],
  );

  const { defaultLayout, onLayoutChanged } = useDefaultLayout({
    id: 'mainLayout',
    panelIds,
    storage: typeof window !== 'undefined' ? localStorage : undefined,
  });

  // Sum of the rendered fixed panels' min widths. The hook adds the center panel's floor.
  const minTotalWidth =
    (showSideRail ? SIDEBAR_MIN_WIDTH : 0) +
    (showSideRail && search ? SEARCH_OPTIONS_MIN_WIDTH : 0) +
    (!isManageWelcomePage && displayInspector ? INSPECTOR_MIN_WIDTH : 0);
  const { groupRef: groupElementRef, minWidth } = useFixedPanelsGroupMinWidth({
    flexPanelId: 'main-content',
    minTotalWidth,
    flexPanelMinWidth: MAIN_CONTENT_MIN_WIDTH,
  });
  const resizableGroupRef = useGroupRef();
  const safeDefaultLayout = useMemo(
    () => ((defaultLayout?.['inspector'] ?? 1) < 1 ? undefined : defaultLayout),
    [defaultLayout],
  );
  const lastExpandedInspectorSizeRef = useRef(
    Math.max(safeDefaultLayout?.['inspector'] ?? INSPECTOR_DEFAULT_SIZE_PERCENT, 1),
  );
  const panelGroupKey = useMemo(
    () =>
      [
        showSideRail ? 'side-rail' : 'no-side-rail',
        showSideRail && search ? 'search-options' : 'no-search-options',
        !isManageWelcomePage && canDisplayInspector ? 'inspector' : 'no-inspector',
      ].join('|'),
    [canDisplayInspector, isManageWelcomePage, search, showSideRail],
  );

  // Drag-to-close: dragging the inspector handle past its minSize collapses it to 0 width. We close
  // the inspector when the drag ENDS (pointerup) if it was left collapsed — not during the drag,
  // because unmounting the panel/separator mid-gesture makes v4 call setPointerCapture on a removed
  // element (InvalidStateError) and corrupts its drag state. We track the panel's last reported size
  // (updated by onResize) and decide on release; this avoids depending on v4's prevSize arg or an
  // exact-zero report. The group's reserved min-width keeps window resize from ever collapsing it,
  // so only an explicit drag can reach 0. Seeded to Infinity so a stray pointerup never closes it.
  const inspectorSizeRef = useRef(Number.POSITIVE_INFINITY);
  useEffect(() => {
    const handlePointerUp = () => {
      if (inspectorSizeRef.current < 1) {
        inspectorSizeRef.current = Number.POSITIVE_INFINITY;
        setShowInspector(false);
      }
    };
    window.addEventListener('pointerup', handlePointerUp);
    return () => window.removeEventListener('pointerup', handlePointerUp);
  }, [setShowInspector]);

  useEffect(() => {
    if (!displayInspector) {
      return;
    }

    let retryId: ReturnType<typeof setTimeout> | undefined;

    const expandRestoredCollapsedInspector = (canRetry: boolean) => {
      const layout = resizableGroupRef.current?.getLayout();
      if (layout?.['inspector'] === undefined) {
        if (canRetry) {
          retryId = setTimeout(() => expandRestoredCollapsedInspector(false), 0);
        }
        return;
      }

      if (layout['inspector'] >= 1) {
        return;
      }

      const inspectorSize = lastExpandedInspectorSizeRef.current;
      resizableGroupRef.current?.setLayout({
        ...layout,
        'main-content': Math.max(0, (layout['main-content'] ?? 0) - inspectorSize),
        'inspector': inspectorSize,
      });
    };

    expandRestoredCollapsedInspector(true);

    return () => {
      if (retryId !== undefined) {
        clearTimeout(retryId);
      }
    };
  }, [displayInspector, resizableGroupRef]);

  // Persist the layout, but never store a collapsed inspector (collapsedSize 0%). Otherwise v4
  // restores it collapsed on the next mount and reopening via the toolbar appears to do nothing.
  // Skipping the collapsed layout keeps the last expanded width for a proper reopen. We also ignore
  // any already-stored collapsed inspector default layout, and the reopen correction above handles
  // v4's in-memory panel-set cache if it restores the inspector at 0%.
  const handleLayoutChanged = (layout: Layout) => {
    if ((layout['inspector'] ?? 1) < 1) {
      return;
    }
    lastExpandedInspectorSizeRef.current = layout['inspector'] ?? lastExpandedInspectorSizeRef.current;
    onLayoutChanged(layout);
  };

  const panelGroup = (
    <ResizablePanelGroup
      key={panelGroupKey}
      elementRef={groupElementRef}
      groupRef={resizableGroupRef}
      style={{ minWidth }}
      defaultLayout={safeDefaultLayout}
      onLayoutChanged={handleLayoutChanged}
    >
      {showSideRail && (
        <>
          <ResizablePanel
            id="sidebar"
            defaultSize="15%"
            minSize={SIDEBAR_MIN_WIDTH}
            collapsedSize="5%"
            groupResizeBehavior="preserve-pixel-size"
          >
            <Sidebar rowCount={assets?.pagination?.count} />
          </ResizablePanel>
          <ResizableHandle />
          {search && (
            <>
              <ResizablePanel
                id="search-options"
                defaultSize="15%"
                minSize={SEARCH_OPTIONS_MIN_WIDTH}
                collapsedSize="5%"
                groupResizeBehavior="preserve-pixel-size"
              >
                <SearchOptions />
              </ResizablePanel>
              <ResizableHandle />
            </>
          )}
        </>
      )}

      <ResizablePanel
        id="main-content"
        minSize={MAIN_CONTENT_MIN_WIDTH}
        className="@container/main relative flex h-full flex-col"
      >
        {Children.map(children, (child) =>
          isValidElement(child) ? cloneElement(child as ReactElement<GalleryLayoutProps>) : child,
        )}
      </ResizablePanel>
      {!isManageWelcomePage && displayInspector && (
        <>
          <ResizableHandle />
          <ResizablePanel
            id="inspector"
            defaultSize="20%"
            minSize={INSPECTOR_MIN_WIDTH}
            collapsible
            collapsedSize="0%"
            groupResizeBehavior="preserve-pixel-size"
            // Record the current size: the pointerup handler closes the inspector if it was left
            // collapsed.
            onResize={(size: PanelSize) => {
              inspectorSizeRef.current = size.inPixels;
            }}
          >
            <div className="flex h-full flex-col">
              <div className="min-h-0 flex-1 overflow-hidden">
                <Inspector
                  data={assets?.assets ?? []}
                  pagination={assets?.pagination}
                  singleAssetShare={singleAssetShare}
                  singleAssetView={singleAssetView}
                  isUpload={isUpload}
                  uploadTabContent={uploadTabContent}
                  contributorMetadataDefinitions={contributorMetadataDefinitions}
                />
              </div>
            </div>
          </ResizablePanel>
        </>
      )}
    </ResizablePanelGroup>
  );

  const renderLayout = () => (
    <div className={cn('flex h-screen min-h-screen flex-col space-y-px bg-ui-void-gap')}>
      <Navigation
        node={node}
        singleAssetView={singleAssetView}
        singleAssetShare={singleAssetShare}
        parentPath={parentPath}
      />

      <div className="flex flex-1 space-x-px overflow-hidden">
        <SideNavigation />
        {pageFooter ? (
          <div className="flex min-w-0 flex-1 flex-col space-y-px overflow-hidden">
            <div className="flex flex-1 space-x-px overflow-hidden">{panelGroup}</div>
            {pageFooter}
          </div>
        ) : (
          panelGroup
        )}
        <UploadDialog refetchNode={refetchNode} />
      </div>
    </div>
  );

  return (
    <div key={activeUploadSessionId} className="relative" {...dropzoneProps}>
      {isDragActive && !isDropzoneDisabled && (
        <div className="pointer-events-auto fixed inset-0 z-1000 flex flex-col items-center justify-center gap-y-3 bg-black/60 py-4 backdrop-blur-xs">
          <Download className="icon-64 text-white dark:text-icon-base" strokeWidth={0.5} />
          <div className="text-2xl font-normal text-white dark:text-text-base">{tUpload('dropFilesHere')}</div>
          <div className="text-sm text-white opacity-80 dark:text-text-gray">
            {selectedNode?.node ? (
              <div className="flex items-center gap-x-2">
                {tUpload('filesWillBeUploadedTo')}
                <FolderIcon /> <strong className="line-clamp-1 max-w-60 truncate">{selectedNode.node.name}</strong>
              </div>
            ) : (
              tUpload('chooseDestinationLater')
            )}
          </div>
        </div>
      )}
      {renderLayout()}
    </div>
  );
};
